import { useEffect, useState, useRef } from "react";
import { useNavigate, useOutletContext, useLocation } from "react-router-dom";
import { apiFetch } from "../../../config/api";
import { getUser } from "../../../config/auth";
import { canValidateTasks, canBulkUpload } from "../../../config/rbac";
import FilterChip from "../../../components/FilterChip";
import Pagination from "../../../components/Pagination";
import BulkTaskUploadModal from "../../../components/BulkTaskUploadModal";
import Loader from "../../../components/Loader";
import Toast, { useToast } from "../../../components/Toast";
import StatusPill from "../../../components/StatusPill";
import PriorityBadge from "../../../components/PriorityBadge";
import EmptyState from "../../../components/EmptyState";
import PageHeader from "../../../components/PageHeader";

const PAGE_SIZE = 10;

function formatDate(date) {
  if (!date) return "—";
  const d = new Date(date);
  const now = new Date();
  const diff = Math.floor((d - now) / 86400000);
  const str = d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  if (diff < 0) return { text: str, overdue: true };
  if (diff === 0) return { text: "Today", urgent: true };
  if (diff === 1) return { text: "Tomorrow", urgent: true };
  return { text: str };
}

function DateCell({ date }) {
  if (!date) return <span style={{ color: "var(--text-3)" }}>—</span>;
  const result = formatDate(date);
  if (typeof result === "string") return <span>{result}</span>;
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: result.overdue || result.urgent ? 600 : 400,
        color: result.overdue
          ? "#dc2626"
          : result.urgent
            ? "#d97706"
            : "var(--text-2)",
      }}
    >
      {result.overdue && <span style={{ marginRight: 3 }}>⚠</span>}
      {result.text}
    </span>
  );
}

export default function TasksPage() {
  const { refreshKey } = useOutletContext();
  const [tasks, setTasks] = useState([]);       // current page items (from server)
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statusCounts, setStatusCounts] = useState({});  // all visible tasks, pre-filter
  const [overdueCountAll, setOverdueCountAll] = useState(0);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const { toast, showToast, dismissToast } = useToast(2500);
  const fetchIdRef = useRef(0);

  const [projectId, setProjectId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [search, setSearch] = useState("");
  // FIX BUG-5: track overdue-only filter applied from dashboard drill-down
  const [overdueFilter, setOverdueFilter] = useState(false);
  const [periodFilter, setPeriodFilter] = useState("");

  const navigate = useNavigate();
  // FIX BUG-5: read URL search params so dashboard drill-down links pre-populate filters
  const location = useLocation();

  const [editingCell, setEditingCell] = useState(null); // { taskId, field }
  const [savingTasks, setSavingTasks] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  function isRedFlag(task) {
    if (task.status === "Completed" || !task.dueDate || !task.createdAt) return false;
    const total = new Date(task.dueDate) - new Date(task.createdAt);
    if (total <= 0) return false;
    return (Date.now() - new Date(task.createdAt)) / total >= 0.8;
  }

  async function exportCSV() {
    setExporting(true);
    try {
      const endpoint = role === "Staff" ? "/api/tasks/my" : "/api/tasks/visible";
      const params = new URLSearchParams({ limit: "1000" });
      if (search.trim())   params.set("search", search.trim());
      if (statusFilter)    params.set("status", statusFilter);
      if (priorityFilter)  params.set("priority", priorityFilter);
      if (ownerFilter)     params.set("ownerId", ownerFilter);
      if (deptFilter)      params.set("deptId", deptFilter);
      if (projectId)       params.set("projectId", projectId);
      if (overdueFilter)   params.set("overdue", "true");
      if (periodFilter)    params.set("period", periodFilter);
      const res = await apiFetch(`${endpoint}?${params}`);
      const data = await res.json();
      const rows = data.items || [];
      const headers = ["Title", "Status", "Priority", "Assignee", "Department", "Project", "Due Date", "Created At", "Completed At"];
      const csv = [headers, ...rows.map(t => [
        `"${(t.title || "").replace(/"/g, '""')}"`,
        t.status, t.priority,
        `"${t.assignedToUserName || ""}"`,
        `"${t.assignedToUserDepartmentName || ""}"`,
        `"${t.projectName || ""}"`,
        t.dueDate ? new Date(t.dueDate).toLocaleDateString("en-IN") : "",
        t.createdAt ? new Date(t.createdAt).toLocaleDateString("en-IN") : "",
        t.completedAt ? new Date(t.completedAt).toLocaleDateString("en-IN") : "",
      ])].map(r => r.join(",")).join("\n");
      const a = Object.assign(document.createElement("a"), {
        href: URL.createObjectURL(new Blob([csv], { type: "text/csv" })),
        download: `tasks-${new Date().toISOString().slice(0, 10)}.csv`,
      });
      a.click();
    } finally {
      setExporting(false);
    }
  }

  const user = getUser();
  const role = user?.role || "Staff";
  const isPrivileged = role !== "Staff";
  const canValidate = canValidateTasks(role);
  const canBulk = canBulkUpload(role);

  // Returns which status values are selectable from the current one
  function getStatusOptions(currentStatus) {
    const transitions = {
      Open:            ["Open", "InProgress", "Hold"],
      InProgress:      ["InProgress", "UnderValidation", "Hold"],
      Hold:            ["Hold", "Open", "InProgress"],
      UnderValidation: canValidate ? ["UnderValidation", "Completed", "InProgress", "Hold"] : ["UnderValidation", "InProgress", "Hold"],
      Completed:       role === "Admin" ? ["Completed", "Open"] : ["Completed"],
    };
    const labels = { Open: "Open", InProgress: "In Progress", Hold: "On Hold", UnderValidation: "Under Validation", Completed: "Completed" };
    const reachable = transitions[currentStatus] || [currentStatus];
    return reachable.map(s => ({ value: s, label: labels[s] }));
  }

  async function updateStatus(task, newStatus) {
    if (newStatus === task.status) { setEditingCell(null); return; }
    setSavingTasks(s => { const n = new Set(s); n.add(task.id); return n; });
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
    try {
      const res = await apiFetch(`/api/tasks/${task.id}/status`, {
        method: "PUT",
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error(await res.text());
      showToast("Status updated");
    } catch (err) {
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: task.status } : t));
      showToast(err.message || "Failed to update status", "error");
    } finally {
      setSavingTasks(s => { const n = new Set(s); n.delete(task.id); return n; });
      setEditingCell(null);
    }
  }

  async function updateField(task, field, value) {
    if (String(task[field] ?? "") === String(value ?? "")) { setEditingCell(null); return; }
    // Build optimistic display update (for assignee, also update display name/dept)
    let optimistic = { [field]: value };
    if (field === "assignedToUserId") {
      const u = users.find(x => x.id === value);
      if (u) {
        optimistic.assignedToUserName = u.name;
        optimistic.assignedToUserDepartmentId = u.departmentId || null;
        optimistic.assignedToUserDepartmentName = u.departmentName || null;
      }
    }
    setSavingTasks(s => { const n = new Set(s); n.add(task.id); return n; });
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, ...optimistic } : t));
    try {
      const res = await apiFetch(`/api/tasks/${task.id}`, {
        method: "PUT",
        body: JSON.stringify({
          title: task.title,
          projectId: task.projectId || null,
          assignedToUserId: field === "assignedToUserId" ? value : task.assignedToUserId,
          dueDate: field === "dueDate" ? (value || null) : (task.dueDate || null),
          priority: field === "priority" ? value : (task.priority || "Medium"),
          status: task.status,
          expectedOutcome: task.expectedOutcome || null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      showToast("Saved");
    } catch (err) {
      setTasks(prev => prev.map(t => t.id === task.id ? task : t));
      showToast(err.message || "Failed to update task", "error");
    } finally {
      setSavingTasks(s => { const n = new Set(s); n.delete(task.id); return n; });
      setEditingCell(null);
    }
  }

  // FIX BUG-5: on mount (or URL change), read ?status=, ?overdue=1, ?dept=, ?period=, ?projectId= from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!location.search) setSearch("");
    const s = params.get("status");
    const o = params.get("overdue");
    const d = params.get("dept");
    const p = params.get("period");
    const proj = params.get("projectId");
    const q = params.get("search") || params.get("q");
    if (s) setStatusFilter(s);
    if (o === "1") setOverdueFilter(true);
    if (d) setDeptFilter(d);
    if (p) setPeriodFilter(p);
    if (proj) setProjectId(proj);
    if (q !== null) setSearch(q);
  }, [location.search]);

  const activeFilterCount = [
    statusFilter,
    priorityFilter,
    ownerFilter,
    deptFilter,
    projectId,
    search,
    overdueFilter ? "overdue" : "",
    periodFilter,
  ].filter(Boolean).length;

  function clearFilters() {
    setStatusFilter("");
    setPriorityFilter("");
    setOwnerFilter("");
    setDeptFilter("");
    setProjectId("");
    setSearch("");
    setOverdueFilter(false);
    setPeriodFilter("");
    setPage(1);
    // Clear URL query params so back-navigation doesn't re-apply stale filters
    if (location.search) navigate(location.pathname, { replace: true });
  }

  useEffect(() => {
    Promise.all([
      apiFetch("/api/projects")
        .then((r) => r.json())
        .catch(() => []),
      apiFetch("/api/departments")
        .then((r) => r.json())
        .catch(() => []),
      apiFetch("/api/users/assignable")
        .then((r) => r.json())
        .catch(() => []),
    ]).then(([p, d, u]) => {
      setProjects(Array.isArray(p) ? p : []);
      setDepartments(Array.isArray(d) ? d : []);
      setUsers(Array.isArray(u) ? u : []);
    });
  }, []);

  // Server-side fetch: fires when any filter, page, or refreshKey changes
  useEffect(() => {
    const myId = ++fetchIdRef.current;
    setLoading(true);
    const endpoint = role === "Staff" ? "/api/tasks/my" : "/api/tasks/visible";
    const params = new URLSearchParams({ page, limit: PAGE_SIZE });
    if (search.trim())    params.set("search", search.trim());
    if (statusFilter)    params.set("status", statusFilter);
    if (priorityFilter)  params.set("priority", priorityFilter);
    if (ownerFilter)     params.set("ownerId", ownerFilter);
    if (deptFilter)      params.set("deptId", deptFilter);
    if (projectId)       params.set("projectId", projectId);
    if (overdueFilter)   params.set("overdue", "true");
    if (periodFilter)    params.set("period", periodFilter);

    apiFetch(`${endpoint}?${params}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((d) => {
        if (fetchIdRef.current !== myId) return; // stale response — discard
        setTasks(d.items || []);
        setTotalCount(d.totalCount ?? 0);
        setTotalPages(d.totalPages ?? 1);
        setStatusCounts(d.statusCounts || {});
        setOverdueCountAll(d.overdueCount ?? 0);
        setError("");
      })
      .catch(() => {
        if (fetchIdRef.current === myId) setError("Failed to load tasks.");
      })
      .finally(() => {
        if (fetchIdRef.current === myId) setLoading(false);
      });
  }, [refreshKey, fetchTrigger, role, page, search, statusFilter, priorityFilter, ownerFilter, deptFilter, projectId, overdueFilter, periodFilter]);

  // Reset to page 1 whenever a filter changes (not on page navigation)
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, priorityFilter, ownerFilter, deptFilter, projectId, overdueFilter, periodFilter]);

  return (
    <div>
      <Toast toast={toast} onDismiss={dismissToast} />

      {/* ── Header ── */}
      <PageHeader
        title={role === "Staff" ? "My Tasks" : "All Tasks"}
        subtitle={
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span>
              {activeFilterCount > 0
                ? `${totalCount} of ${Object.values(statusCounts).reduce((a, b) => a + b, 0)} tasks`
                : `${totalCount} tasks`}
            </span>
            {[
              { label: "In Progress", count: statusCounts["InProgress"] || 0, color: "#c2410c", bg: "#fff3e8" },
              { label: "Under Validation", count: statusCounts["UnderValidation"] || 0, color: "#1d4ed8", bg: "#eff6ff" },
              { label: "On Hold", count: statusCounts["Hold"] || 0, color: "var(--warn-text)", bg: "#fefce8" },
              { label: "Overdue", count: overdueCountAll, color: "var(--danger-text)", bg: "#fef2f2" },
            ]
              .filter((s) => s.count > 0)
              .map((s) => (
                <span key={s.label} style={{ padding: "1px 7px", borderRadius: 999, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color }}>
                  {s.count} {s.label}
                </span>
              ))}
          </div>
        }
        actions={
          <>
            {canBulk && (
              <button
                onClick={() => setShowBulkUpload(true)}
                className="btn-ghost"
                style={{ padding: "9px 16px", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
              >
                📤 Bulk Upload
              </button>
            )}
            <button
              onClick={exportCSV}
              disabled={exporting}
              style={{
                padding: "9px 14px", borderRadius: 10, fontSize: 13, fontWeight: 600,
                background: "var(--surface-2)", color: "var(--text-2)", border: "1px solid var(--border-strong)",
                cursor: exporting ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", opacity: exporting ? 0.7 : 1,
              }}
            >
              ⬇ {exporting ? "Exporting..." : "Export CSV"}
            </button>
          </>
        }
      />

      <BulkTaskUploadModal
        open={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
        onCreated={(count) => {
          showToast(`${count} task${count !== 1 ? "s" : ""} created successfully`);
          setPage(1);
          setFetchTrigger((t) => t + 1); // force re-fetch even if page was already 1
        }}
      />

      {/* ── Filter Bar ── */}
      <div
        style={{
          display: "flex",
          gap: 6,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 14,
          padding: "10px 14px",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 12,
        }}
      >
        {/* Search */}
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "var(--text-3)", pointerEvents: "none" }}>🔍</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tasks..."
            style={{
              padding: "5px 10px 5px 28px",
              borderRadius: 20,
              fontSize: 12,
              border: search ? "1.5px solid var(--brand)" : "1px solid var(--border-strong)",
              background: search ? "var(--brand-soft)" : "var(--surface-2)",
              color: "var(--text-1)",
              outline: "none",
              width: 160,
              transition: "all 0.14s",
            }}
          />
        </div>

        <div style={{ width: 1, height: 18, background: "var(--border-strong)", margin: "0 2px" }} />

        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "var(--text-3)",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            marginRight: 2,
          }}
        >
          Filter
        </span>

        {/* Status */}
        <FilterChip
          value={statusFilter}
          onChange={setStatusFilter}
          label="Status"
          options={[
            { value: "Open", label: "Open" },
            { value: "InProgress", label: "In Progress" },
            { value: "Hold", label: "On Hold" },
            { value: "UnderValidation", label: "Under Validation" },
            { value: "Completed", label: "Completed" },
          ]}
        />

        {/* Priority */}
        <FilterChip
          value={priorityFilter}
          onChange={setPriorityFilter}
          label="Priority"
          options={[
            { value: "High", label: "🔴 High" },
            { value: "Medium", label: "🟡 Medium" },
            { value: "Low", label: "🟢 Low" },
          ]}
        />

        {/* Owner (privileged only) */}
        {isPrivileged && users.length > 0 && (
          <FilterChip
            value={ownerFilter}
            onChange={setOwnerFilter}
            label="Assigned To"
            options={users.map((u) => ({ value: u.id, label: u.name }))}
          />
        )}

        {/* Department — only top-level departments shown; backend includes sub-depts */}
        {isPrivileged && departments.length > 0 && (
          <FilterChip
            value={deptFilter}
            onChange={setDeptFilter}
            label="Department"
            options={departments.filter(d => !d.parentDepartmentId).map((d) => ({ value: d.id, label: d.name }))}
          />
        )}

        {/* Project */}
        {isPrivileged && projects.length > 0 && (
          <FilterChip
            value={projectId}
            onChange={setProjectId}
            label="Project"
            options={projects.map((p) => ({ value: p.id, label: p.name }))}
          />
        )}

        {/* Period chip — shown when navigated from dashboard with a period filter */}
        {periodFilter && (
          <button
            onClick={() => setPeriodFilter("")}
            style={{
              padding: "5px 10px",
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 600,
              background: "rgba(99,102,241,0.1)",
              color: "#6366f1",
              border: "1.5px solid #a5b4fc",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            📅 {periodFilter.charAt(0).toUpperCase() + periodFilter.slice(1)} <span style={{ fontSize: 10 }}>✕</span>
          </button>
        )}

        {/* FIX BUG-5: Show Overdue active chip when filter was applied from dashboard drill-down */}
        {overdueFilter && (
          <button
            onClick={() => setOverdueFilter(false)}
            style={{
              padding: "5px 10px",
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 600,
              background: "var(--danger-bg)",
              color: "var(--danger-text)",
              border: "1.5px solid #fca5a5",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            ⚠ Overdue <span style={{ fontSize: 10 }}>✕</span>
          </button>
        )}

        {/* Clear all */}
        {activeFilterCount > 0 && (
          <>
            <div
              style={{
                width: 1,
                height: 18,
                background: "var(--border-strong)",
                margin: "0 4px",
              }}
            />
            <button
              onClick={clearFilters}
              style={{
                padding: "5px 10px",
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                background: "var(--danger-bg)",
                color: "var(--danger-text)",
                border: "1px solid #fecdd3",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <span style={{ fontSize: 10 }}>✕</span>
              Clear {activeFilterCount > 1 ? `(${activeFilterCount})` : ""}
            </button>
          </>
        )}
      </div>

      {error && (
        <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 8, background: "var(--danger-soft)", border: "1px solid var(--danger-border)", color: "var(--danger-text)", fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* ── Table ── */}
      <div className="glass-card" style={{ overflow: "hidden" }}>
        <div className="tasks-table">
          <div
            className="tasks-row header"
            style={{
              gridTemplateColumns: "2.2fr 1.1fr 1fr 0.85fr 0.75fr 0.9fr",
            }}
          >
            <div>Task</div>
            <div>Assigned To</div>
            <div>Department</div>
            <div>Due Date</div>
            <div>Priority</div>
            <div>Status</div>
          </div>

          {loading && <Loader.TableRows rows={8} />}

          {!loading && totalCount === 0 && (
            <EmptyState
              icon="📋"
              title={activeFilterCount > 0 ? "No tasks match your filters" : isPrivileged ? "No tasks created yet" : "No tasks assigned to you yet"}
              subtitle={activeFilterCount > 0 ? "Try adjusting or clearing your filters." : isPrivileged ? "Use the Bulk Upload button to import tasks, or ask your admin to create tasks." : "Tasks assigned to you will appear here."}
              action={activeFilterCount > 0 && (
                <button onClick={clearFilters} style={{ padding: "7px 16px", fontSize: 13, borderRadius: 8, background: "var(--brand-soft)", color: "var(--brand)", border: "1px solid var(--brand-border)", fontWeight: 600 }}>
                  Clear filters
                </button>
              )}
            />
          )}

          {!loading &&
            tasks.map((task) => {
              const isOverdue =
                task.dueDate &&
                new Date(task.dueDate) < new Date() &&
                task.status !== "Completed";
              const isSaving = savingTasks.has(task.id);
              const editing = (field) =>
                editingCell?.taskId === task.id && editingCell?.field === field;

              return (
                <div
                  key={task.id}
                  className="tasks-row"
                  style={{
                    gridTemplateColumns: "2.2fr 1.1fr 1fr 0.85fr 0.75fr 0.9fr",
                    opacity: isSaving ? 0.7 : 1,
                    transition: "opacity 0.15s",
                    cursor: "pointer",
                  }}
                  onClick={() => navigate(`/tasks/${task.id}`)}
                >
                  {/* ── Task title — click to open detail ── */}
                  <div>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: task.projectName ? 2 : 0, minWidth: 0, overflow: "hidden" }}
                    >
                      <div
                        className="task-title"
                        title={task.title}
                        style={{
                          cursor: "pointer",
                          textDecoration: "underline",
                          textDecorationColor: "transparent",
                          textUnderlineOffset: 2,
                        }}
                        onMouseEnter={e => e.currentTarget.style.textDecorationColor = "currentColor"}
                        onMouseLeave={e => e.currentTarget.style.textDecorationColor = "transparent"}
                        onClick={() => navigate(`/tasks/${task.id}`)}
                      >
                        {task.title}
                      </div>
                      {isRedFlag(task) && (
                        <span title="At risk: 80%+ of timeline elapsed with no completion" style={{ fontSize: 13, flexShrink: 0 }}>🚩</span>
                      )}
                    </div>
                    {task.projectName && (
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--text-3)",
                          display: "flex",
                          alignItems: "center",
                          gap: 3,
                        }}
                      >
                        <span style={{ opacity: 0.6 }}>▦</span>{" "}
                        {task.projectName}
                      </div>
                    )}
                  </div>

                  {/* ── Assignee — inline select (privileged only) ── */}
                  <div
                    className="assignee"
                    style={{ cursor: isPrivileged && !isSaving ? "pointer" : "default" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isPrivileged && !isSaving && !editing("assignedToUserId"))
                        setEditingCell({ taskId: task.id, field: "assignedToUserId" });
                    }}
                  >
                    {editing("assignedToUserId") ? (
                      <select
                        autoFocus
                        defaultValue={task.assignedToUserId}
                        onChange={e => updateField(task, "assignedToUserId", e.target.value)}
                        onBlur={() => setEditingCell(null)}
                        onKeyDown={e => e.key === "Escape" && setEditingCell(null)}
                        onClick={e => e.stopPropagation()}
                        style={{ fontSize: 12, borderRadius: 6, padding: "2px 6px", width: "100%" }}
                      >
                        {users.map(u => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </select>
                    ) : (
                      <>
                        <div className="avatar">
                          {(task.assignedToUserName || "?")[0].toUpperCase()}
                        </div>
                        <span style={{ fontSize: 12 }}>
                          {task.assignedToUserName || "Unassigned"}
                        </span>
                        {isPrivileged && (
                          <span style={{ fontSize: 9, color: "var(--text-3)", marginLeft: 2 }}>✎</span>
                        )}
                      </>
                    )}
                  </div>

                  {/* ── Department — read-only ── */}
                  <div style={{ fontSize: 12, color: "var(--text-2)" }}>
                    {task.assignedToUserDepartmentName ? (
                      <span
                        style={{
                          display: "inline-block",
                          padding: "2px 8px",
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 500,
                          background: "var(--surface-2, #f3f4f6)",
                          color: "var(--text-2, #6b7280)",
                          border: "1px solid var(--border, #e5e7eb)",
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {task.assignedToUserDepartmentName}
                      </span>
                    ) : (
                      <span style={{ color: "var(--text-3)" }}>—</span>
                    )}
                  </div>

                  {/* ── Due Date — inline date input (privileged only) ── */}
                  <div
                    style={{ cursor: isPrivileged && !isSaving ? "pointer" : "default" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isPrivileged && !isSaving && !editing("dueDate"))
                        setEditingCell({ taskId: task.id, field: "dueDate" });
                    }}
                  >
                    {editing("dueDate") ? (
                      <input
                        autoFocus
                        type="date"
                        defaultValue={task.dueDate ? task.dueDate.split("T")[0] : ""}
                        min={new Date().toLocaleDateString('en-CA')}
                        onChange={e => updateField(task, "dueDate", e.target.value)}
                        onBlur={() => setEditingCell(null)}
                        onKeyDown={e => e.key === "Escape" && setEditingCell(null)}
                        onClick={e => e.stopPropagation()}
                        style={{ fontSize: 12, borderRadius: 6, padding: "2px 4px", width: "100%" }}
                      />
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <DateCell date={task.dueDate} />
                        {isPrivileged && (
                          <span style={{ fontSize: 9, color: "var(--text-3)" }}>✎</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── Priority — inline select (privileged only) ── */}
                  <div
                    style={{ cursor: isPrivileged && !isSaving ? "pointer" : "default" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isPrivileged && !isSaving && !editing("priority"))
                        setEditingCell({ taskId: task.id, field: "priority" });
                    }}
                  >
                    {editing("priority") ? (
                      <select
                        autoFocus
                        defaultValue={task.priority}
                        onChange={e => updateField(task, "priority", e.target.value)}
                        onBlur={() => setEditingCell(null)}
                        onKeyDown={e => e.key === "Escape" && setEditingCell(null)}
                        onClick={e => e.stopPropagation()}
                        style={{ fontSize: 12, borderRadius: 6, padding: "2px 4px" }}
                      >
                        <option value="High">🔴 High</option>
                        <option value="Medium">🟡 Medium</option>
                        <option value="Low">🟢 Low</option>
                      </select>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <PriorityBadge priority={task.priority} />
                        {isPrivileged && (
                          <span style={{ fontSize: 9, color: "var(--text-3)" }}>✎</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── Status — inline select (all users, workflow-enforced) ── */}
                  <div
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      cursor: !isSaving && (task.status !== "Completed" || role === "Admin") ? "pointer" : "default",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isSaving && (task.status !== "Completed" || role === "Admin") && !editing("status"))
                        setEditingCell({ taskId: task.id, field: "status" });
                    }}
                  >
                    {editing("status") ? (
                      <select
                        autoFocus
                        defaultValue={task.status}
                        onChange={e => updateStatus(task, e.target.value)}
                        onBlur={() => setEditingCell(null)}
                        onKeyDown={e => e.key === "Escape" && setEditingCell(null)}
                        onClick={e => e.stopPropagation()}
                        style={{ fontSize: 12, borderRadius: 6, padding: "2px 4px" }}
                      >
                        {getStatusOptions(task.status).map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    ) : (
                      <>
                        <StatusPill status={task.status} />
                        {isOverdue && task.status !== "Completed" && (
                          <span style={{ fontSize: 10, color: "var(--danger-text)" }} title="Overdue">⚠</span>
                        )}
                        {(task.status !== "Completed" || role === "Admin") && (
                          <span style={{ fontSize: 9, color: "var(--text-3)" }}>✎</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
        </div>

        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={(p) => setPage(Math.max(1, Math.min(p, totalPages)))}
          total={totalCount}
          pageSize={PAGE_SIZE}
        />
      </div>
    </div>
  );
}


